import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { Translation, Locale } from 'locale'
import { HaircutBand, HaircutEntryDetail } from 'api'
import { NumericInput } from 'view/components/inputs'
import {
  gridOptionsBase,
  groupingRowColDef,
  TypedValueGetterParams,
  createGroupedActionRenderer,
  withAllContext,
  createActionsRenderer,
  StandardGridColumnId
} from 'view/components/datagrid'
import {
  makeHaircutFormatter,
  makeHaircutFilterGetter,
  getHaircutFormatOptions,
  makeNumberQuickFilterTextGetter
} from 'view/shared/datagrid'
import { HaircutEntryActionsButton } from './HaircutEntryActionsButton'
import { HaircutBandHelper, HaircutTypeHelper, ProductHelper, ProductSectorHelper } from 'model'

enum HaircutGridColumnId {
  HAIRCUT_MY_FIRM = 'HAIRCUT_MY_FIRM',
  HAIRCUT_MY_ENTITY = 'HAIRCUT_MY_ENTITY',
  HAIRCUT_COUNTERPARTY_FIRM = 'HAIRCUT_COUNTERPARTY_FIRM',
  HAIRCUT_COUNTERPARTY = 'HAIRCUT_COUNTERPARTY',
  HAIRCUT_PRODUCT = 'HAIRCUT_PRODUCT',
  HAIRCUT_SECTOR = 'HAIRCUT_SECTOR',
  HAIRCUT_COLLATERAL_BAND = 'HAIRCUT_COLLATERAL_BAND',
  HAIRCUT_COLLATERAL_BAND_MIN = 'HAIRCUT_COLLATERAL_BAND_MIN',
  HAIRCUT_COLLATERAL_BAND_MAX = 'HAIRCUT_COLLATERAL_BAND_MAX',
  HAIRCUT_HAIRCUT_TYPE = 'HAIRCUT_HAIRCUT_TYPE',
  HAIRCUT_BAND = 'HAIRCUT_BAND'
}

export const HAIRCUT_VALUES_FIELDNAME = 'haircutEntry.valuesByBand'

export const createHaircutGridColumnDefs: (haircutBands: HaircutBand[]) => (ColGroupDef | ColDef)[] = haircutBands => {
  const fixedColumnDefs = [
    {
      colId: StandardGridColumnId.CHECKBOX,
      headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
      width: 36,
      maxWidth: 36,
      pinned: 'left' as const,
      lockPinned: true,
      enableRowGroup: false,
      lockPosition: true,
      lockVisible: true,
      filter: false,
      resizable: false,
      sortable: false
    },
    groupingRowColDef,
    {
      colId: StandardGridColumnId.ACTIONS,
      equals: () => false,
      headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
      width: 130,
      minWidth: 130,
      headerComponent: withAllContext(createGroupedActionRenderer(HaircutEntryActionsButton)),
      cellClass: 'action-cell',
      cellRenderer: withAllContext(createActionsRenderer(HaircutEntryActionsButton)),
      pinned: 'left' as const,
      lockPinned: true,
      enableRowGroup: false,
      lockVisible: true,
      filter: false,
      sortable: false
    },
    {
      colId: HaircutGridColumnId.HAIRCUT_MY_FIRM,
      valueGetter: (params: TypedValueGetterParams<HaircutEntryDetail>) =>
        params.data && params.data.responderOrganisation && params.data.responderOrganisation.legalName,
      headerName: Locale.translate(Translation.TC_MY_FIRM),
      width: 140,
      filter: 'agSetColumnFilter'
    },
    {
      colId: HaircutGridColumnId.HAIRCUT_MY_ENTITY,
      valueGetter: (params: TypedValueGetterParams<HaircutEntryDetail>) =>
        params.data && params.data.responderTradingEntity
          ? params.data.responderTradingEntity.publishedName
          : Locale.translate(Translation.TC_HAIRCUT_ALL_VALUES),
      headerName: Locale.translate(Translation.TC_MY_ENTITY),
      width: 140,
      filter: 'agSetColumnFilter'
    },
    {
      colId: HaircutGridColumnId.HAIRCUT_COUNTERPARTY_FIRM,
      valueGetter: (params: TypedValueGetterParams<HaircutEntryDetail>) =>
        params.data && params.data.initiatorOrganisation
          ? params.data.initiatorOrganisation.legalName
          : Locale.translate(Translation.TC_HAIRCUT_ALL_VALUES),
      headerName: Locale.translate(Translation.TC_COUNTERPARTY_FIRM),
      width: 140,
      filter: 'agSetColumnFilter'
    },
    {
      colId: HaircutGridColumnId.HAIRCUT_COUNTERPARTY,
      valueGetter: (params: TypedValueGetterParams<HaircutEntryDetail>) =>
        params.data && params.data.initiatorTradingEntity
          ? params.data.initiatorTradingEntity.alias || params.data.initiatorTradingEntity.publishedName
          : Locale.translate(Translation.TC_HAIRCUT_ALL_VALUES),
      headerName: Locale.translate(Translation.TC_COUNTERPARTY),
      width: 140,
      filter: 'agSetColumnFilter'
    },
    {
      colId: HaircutGridColumnId.HAIRCUT_PRODUCT,
      valueGetter: (params: TypedValueGetterParams<HaircutEntryDetail>) =>
        params.data && params.data.product
          ? ProductHelper.getName(params.data.product)
          : Locale.translate(Translation.TC_HAIRCUT_ALL_VALUES),
      headerName: Locale.translate(Translation.TC_PRODUCT),
      width: 140,
      filter: 'agSetColumnFilter'
    },
    {
      colId: HaircutGridColumnId.HAIRCUT_SECTOR,
      valueGetter: (params: TypedValueGetterParams<HaircutEntryDetail>) =>
        params.data && params.data.productSector
          ? ProductSectorHelper.getName(params.data.productSector)
          : Locale.translate(Translation.TC_HAIRCUT_ALL_VALUES),
      headerName: Locale.translate(Translation.TC_SECTOR),
      width: 140,
      filter: 'agSetColumnFilter'
    },
    {
      colId: HaircutGridColumnId.HAIRCUT_COLLATERAL_BAND_MIN,
      valueGetter: (params: TypedValueGetterParams<HaircutEntryDetail>) =>
        params.data && params.data.haircutEntry.collateralMaturityMinYears,
      headerName: Locale.translate(Translation.TC_HAIRCUT_COLLATERAL_BAND_MIN),
      width: 90,
      filter: 'agSetColumnFilter'
    },
    {
      colId: HaircutGridColumnId.HAIRCUT_COLLATERAL_BAND_MAX,
      valueGetter: (params: TypedValueGetterParams<HaircutEntryDetail>) =>
        params.data && params.data.haircutEntry.collateralMaturityMaxYears,
      headerName: Locale.translate(Translation.TC_HAIRCUT_COLLATERAL_BAND_MAX),
      width: 90,
      filter: 'agSetColumnFilter'
    },
    {
      colId: HaircutGridColumnId.HAIRCUT_HAIRCUT_TYPE,
      valueGetter: (params: TypedValueGetterParams<HaircutEntryDetail>) =>
        params.data && HaircutTypeHelper.getName(params.data.haircutEntry.haircutType),
      headerName: Locale.translate(Translation.TC_HAIRCUT_TYPE),
      width: 90,
      filter: 'agSetColumnFilter'
    }
  ]

  const haircutBandColumnDefs = haircutBands.map(
    haircutBand =>
      ({
        colId: `${HaircutGridColumnId.HAIRCUT_BAND}${haircutBand.minDays}`,
        headerName: HaircutBandHelper.getName(haircutBand),
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        width: 130,
        valueGetter: (params: TypedValueGetterParams<HaircutEntryDetail>) =>
          params.data && params.data.haircutEntry?.valuesByBand?.[haircutBand.id],
        valueFormatter: makeHaircutFormatter(),
        filterValueGetter: makeHaircutFilterGetter(),
        getQuickFilterText: makeNumberQuickFilterTextGetter({ percent: true }),
        cellEditorParams: {
          inputComponent: NumericInput,
          inputParams: () => ({
            min: 0,
            formatOptions: { ...getHaircutFormatOptions(), maxDp: 3 },
            parseSpecialCharacters: true
          }),
          updateField: `${HAIRCUT_VALUES_FIELDNAME}[${haircutBand.id}]`
        },
        editable: true,
        enableRowGroup: false
      } as ColDef)
  )

  return [...fixedColumnDefs, ...haircutBandColumnDefs]
}

export const haircutGridOptions: GridOptions<HaircutEntryDetail> = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.haircutEntry.id
}
